import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Keep each role's existing search behaviour in its home page while placing
 * the one visible control in the shared application header. During server
 * rendering (and before the header mounts), the control remains in the page.
 */
export default function RoleHomeSearchPortal({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const initialSlot = document.getElementById('role-home-search-slot');
    if (initialSlot !== null) {
      setSlot(initialSlot);
      return;
    }
    // The header resolves /me independently of the page. Its slot may arrive
    // after the page's first effect, so move the control when it appears.
    const observer = new MutationObserver(() => {
      const nextSlot = document.getElementById('role-home-search-slot');
      if (nextSlot !== null) {
        setSlot(nextSlot);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return <>{slot === null ? children : createPortal(children, slot)}</>;
}
